"use client";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ChevronRightIcon } from "@/components/ui/chevron-right";
import { ChevronsUpDownIcon } from "@/components/ui/chevrons-up-down";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AudioLinesIcon } from "@/components/ui/audio-lines";
import { BrainIcon } from "@/components/ui/brain";
import { BotMessageSquareIcon } from "@/components/ui/bot-message-square";
import { FoldersIcon } from "@/components/ui/folders";
import { UsersIcon } from "@/components/ui/users";
import { MessageSquareDashedIcon } from "@/components/ui/message-square-dashed";
import { PlusIcon } from "@/components/ui/plus";
import { SearchIcon } from "@/components/ui/search";
import { WrenchIcon } from "@/components/ui/wrench";
import { GalleryVerticalEndIcon } from "@/components/ui/gallery-vertical-end";
import { RocketIcon } from "@/components/ui/rocket";
import { ConstructionIcon } from "@/components/ui/construction";
import { BoxesIcon } from "@/components/ui/boxes";
import { GitCommitHorizontalIcon } from "@/components/ui/git-commit-horizontal";
import { ChartSplineIcon }       from "@/components/ui/chart-spline";
import { DollarSignIcon }        from "@/components/ui/dollar-sign";
import { HandHeartIcon }         from "@/components/ui/hand-heart";
import { FileChartLineIcon }     from "@/components/ui/file-chart-line";
import { ArchiveIcon }          from "@/components/ui/archive";
import { ChessKnightIcon }       from "@/components/ui/chess-knight";
import { ChessPawnIcon }         from "@/components/ui/chess-pawn";
import { ChessBishopIcon }       from "@/components/ui/chess-bishop";
import { GavelIcon }             from "@/components/ui/gavel";
import { UserRoundCogIcon }      from "@/components/ui/user-round-cog";
import { SparklesIcon }          from "@/components/ui/sparkles";
import { ActivityIcon }          from "@/components/ui/activity";
import { MailCheckIcon }         from "@/components/ui/mail-check";
import { ZapIcon }               from "@/components/ui/zap";
import { BookOpenTextIcon, Network, Lock, Megaphone, PanelLeftClose, PanelLeftOpen, ChartNoAxesCombined, FlaskConical, CableIcon, EllipsisIcon, PinOffIcon, Share2Icon }  from "lucide-react";
import { toast } from "sonner";
import { NotificationsBell } from "@/components/notifications-bell";
import { CloudCogIcon }            from "@/components/ui/cloud-cog";
import { WorkflowIcon }          from "@/components/ui/workflow";
import { BoxIcon }               from "@/components/ui/box";
import { BotIcon }               from "@/components/ui/bot";
import { SIDEBAR_ROUTES } from "@/lib/sidebar-routes";
import { projectNewChatHref } from "@/lib/chat-routes";
import { unpinProject, usePinnedProjects } from "@/hooks/use-projects";
import { unpinWidget, usePinnedWidgets, type PinnedWidget } from "@/hooks/use-widget-pins";
import type { DbProject } from "@/types/db";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState, type ComponentProps } from "react";

const SIDEBAR_EXPANDED_STORAGE_KEY = "woods-ai:sidebar-expanded";
const PINNED_PROJECTS_OPEN_STORAGE_KEY = "woods-ai:sidebar-pinned-projects-open";
const PINNED_WIDGETS_OPEN_STORAGE_KEY = "woods-ai:sidebar-pinned-widgets-open";

// Defaults to open; only an explicit "false" collapses the pinned list.
const loadStoredPinnedProjectsOpen = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(PINNED_PROJECTS_OPEN_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

const loadStoredPinnedWidgetsOpen = (): boolean => {
  if (typeof window === "undefined") return true;
  try {
    return localStorage.getItem(PINNED_WIDGETS_OPEN_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

// The server always renders collapsed (no access to localStorage), so the
// client's first render must match that exactly or React throws a hydration
// mismatch — this branch changes DOM shape (a header row vs. a lone button),
// not just a class name. The real stored value is applied after mount below.
const loadStoredExpanded = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export type SidebarItem =
  | "new-chat"
  | "search"
  | "team"
  | "projects"
  | "files"
  | "widgets"
  | "prompts"
  | "analytics"
  | "agent-analytics"
  | "model-analytics"
  | "users"
  | "agent-requests"
  | "widget-requests"
  | "file-requests"
  | "documentation"
  | "ai-models"
  | "system"
  | "chats"
  | "agents"
  | "automations"
  | "meetings"
  | "memories"
  | "tools"
  | "policy"
  | "playground"
  | "code"
  | "parse-jobs"
  | "mail-comms"
  | "evaluations"
  | "external-apps"
  | "token-analytics"
  | "roadmap"
  | "archived-projects"
  | "archived-agents"
  | "my-feedback"
  | "notifications"
  | "announcements"
  | "meeting-minutes-admin"
  | "journey";

export type AppSidebarUser = {
  name: string;
  initials: string;
};

export type DbTeamRef = {
  id: string;
  user_id: string;
};

export type AppSidebarProps = Omit<ComponentProps<"aside">, "children"> & {
  activeItem?: SidebarItem;
  onItemClick?: (item: SidebarItem) => void;
  user?: AppSidebarUser;
  userRole?: string | null;
  onProfileClick?: () => void;
  activeTeamId?: string | null;
  teams?: DbTeamRef[];
  sessionUserId?: string | null;
  listMode?: boolean;
  workflowsEnabled?: boolean;
  codeSpacesEnabled?: boolean;
  /** Called after the notifications bell navigates (e.g. to close a mobile Sheet). */
  onNotificationsNavigate?: () => void;
};

type NavItem = {
  id: SidebarItem;
  label: string;
  Icon: React.ElementType;
  minimumRole?: "admin" | "superadmin";
};

const TOP_ITEMS: NavItem[] = [
  { id: "new-chat", label: "New chat", Icon: PlusIcon },
  { id: "code",     label: "Code",     Icon: BoxIcon },
  { id: "search",   label: "Search Chat",   Icon: SearchIcon },
];

const PRIMARY_NAV_ITEMS: NavItem[] = [
  { id: "projects", label: "Projects", Icon: GalleryVerticalEndIcon },
  { id: "agents",   label: "Agents",   Icon: BotMessageSquareIcon },
  { id: "meetings", label: "Meetings", Icon: AudioLinesIcon },
];

const SECONDARY_NAV_ITEMS: NavItem[] = [
  { id: "prompts",    label: "Prompt Library",  Icon: MessageSquareDashedIcon },
  { id: "files",      label: "Library",         Icon: FoldersIcon },
  { id: "widgets",  label: "Widgets",       Icon: BoxesIcon },
];


type MenuItem = {
  id: SidebarItem;
  label: string;
  Icon?: React.ElementType;
  minimumRole?: "admin" | "superadmin";
};

type MenuGroup = {
  heading: string;
  items: MenuItem[];
};

const MENU_GROUPS: MenuGroup[] = [
  {
    heading: "Core",
    items: [
      { id: "memories",      label: "Memories",      Icon: BrainIcon },
      { id: "automations",   label: "Automations",   Icon: ActivityIcon },
      { id: "team",          label: "Teams",         Icon: UsersIcon },
      { id: "journey",       label: "AI Journey",    Icon: RocketIcon },
      { id: "my-feedback",   label: "My Feedback",   Icon: HandHeartIcon },
    ],
  },
  {
    heading: "Monitoring",
    items: [
      { id: "analytics",              label: "Global Analytics",      Icon: ChartSplineIcon,      minimumRole: "admin" },
      { id: "agent-analytics",        label: "Agents Analytics",      Icon: ChartNoAxesCombined,  minimumRole: "admin" },
      { id: "model-analytics",         label: "Model Analytics",       Icon: SparklesIcon,      minimumRole: "superadmin" },
      { id: "token-analytics",         label: "Token Analytics",       Icon: DollarSignIcon,    minimumRole: "superadmin" },
      { id: "chats",                   label: "Feedbacks",              Icon: HandHeartIcon,     minimumRole: "superadmin" },
      { id: "meeting-minutes-admin",   label: "Meetings Analytics",    Icon: AudioLinesIcon,    minimumRole: "superadmin" },
      { id: "parse-jobs",              label: "Large File",            Icon: FileChartLineIcon, minimumRole: "admin" },
      { id: "archived-projects",       label: "Archived Projects",     Icon: ArchiveIcon,       minimumRole: "admin" },
      { id: "archived-agents",         label: "Archived Agents",       Icon: ArchiveIcon,       minimumRole: "admin" },
    ],
  },
  {
    heading: "Transfer Requests",
    items: [
      { id: "agent-requests",    label: "Agent",       Icon: ChessKnightIcon },
      { id: "widget-requests", label: "Widgets", Icon: ChessPawnIcon },
      { id: "file-requests",     label: "Files",      Icon: ChessBishopIcon, minimumRole: "admin" },
    ],
  },
  {
    heading: "Resources",
    items: [
      { id: "documentation",  label: "Documentation",  Icon: BookOpenTextIcon },
      { id: "policy",         label: "AI Policy",      Icon: GavelIcon },
      { id: "roadmap",        label: "Roadmap",        Icon: GitCommitHorizontalIcon },
      { id: "announcements",  label: "Announcements",  Icon: Megaphone },
    ],
  },
  {
    heading: "Admin",
    items: [
      { id: "tools",      label: "Tools",      Icon: WrenchIcon,       minimumRole: "admin" },
      { id: "playground", label: "Playground", Icon: ConstructionIcon, minimumRole: "superadmin" },
      { id: "users",      label: "Users",      Icon: UserRoundCogIcon, minimumRole: "admin" },
      { id: "ai-models",  label: "AI Models",  Icon: SparklesIcon,     minimumRole: "superadmin" },
      { id: "system",     label: "System",     Icon: ActivityIcon,     minimumRole: "superadmin" },
      { id: "mail-comms", label: "Mail Comms", Icon: MailCheckIcon,    minimumRole: "superadmin" },
      { id: "evaluations", label: "Evaluations", Icon: FlaskConical, minimumRole: "superadmin" },
      { id: "external-apps", label: "External Apps", Icon: CableIcon,  minimumRole: "superadmin" },
    ],
  },
];

// ── Workflows manifest ───────────────────────────────────────────────────────

type WorkflowNavEnabled  = { id: string; label: string; href: string; Icon: React.ElementType; enabled: true; minimumRole?: "admin" | "superadmin" };
type WorkflowNavDisabled = { id: string; label: string; Icon: React.ElementType; enabled: false };
type WorkflowNavItem = WorkflowNavEnabled | WorkflowNavDisabled;

const WORKFLOWS_NAV: WorkflowNavItem[] = [
  { id: "managed-agents", label: "Managed Agents", href: "/workflows/managed-agents", Icon: BotIcon,            enabled: true },
  { id: "sessions",       label: "Sessions",        href: "/workflows/sessions",       Icon: ActivityIcon,       enabled: true },
  { id: "triggers",       label: "Triggers",        Icon: ZapIcon,                    enabled: false },
  { id: "environment",    label: "Environment",     Icon: CloudCogIcon,               enabled: false },
  { id: "memories",       label: "Memories",        Icon: BrainIcon,                   enabled: false },
  { id: "multiagent",     label: "Multiagent",      Icon: Network,                     enabled: false },
  { id: "vaults",         label: "Vaults",          Icon: Lock, href: "/workflows/vaults", enabled: true, minimumRole: "superadmin" },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

const roleRank = (role: string | null | undefined) => {
  if (role === "superadmin") return 2;
  if (role === "admin") return 1;
  return 0;
};

const isTeamOwner = (
  itemId: SidebarItem,
  activeTeamId: string | null | undefined,
  teams: DbTeamRef[] | undefined,
  sessionUserId: string | null | undefined
): boolean => {
  if (itemId !== "file-requests" && itemId !== "agent-requests" && itemId !== "widget-requests") return false;
  if (!activeTeamId || !sessionUserId || !teams) return false;
  const team = teams.find((t) => t.id === activeTeamId);
  return team?.user_id === sessionUserId;
};

const hasRequiredRole = (
  itemId: SidebarItem,
  role: string | null | undefined,
  minimumRole?: "admin" | "superadmin",
  activeTeamId?: string | null,
  teams?: DbTeamRef[],
  sessionUserId?: string | null
) => {
  const hasRole = !minimumRole || roleRank(role) >= roleRank(minimumRole);
  if (hasRole) return true;
  return isTeamOwner(itemId, activeTeamId, teams, sessionUserId);
};

const MENU_ITEM_IDS = new Set<SidebarItem>(MENU_GROUPS.flatMap((g) => g.items).map((item) => item.id));

const getSidebarHref = (item: SidebarItem): string | undefined => {
  if (item === "files") {
    return "/?nav=files";
  }

  return SIDEBAR_ROUTES[item];
};

/**
 * Which nav item owns the current URL. Routed pages (SubPageLayout) don't pass
 * `activeItem` — only `home-client` does — so without this nothing is
 * highlighted once you leave "/". Longest matching route wins, so
 * `/agents/requests` highlights "Transfer Requests › Agent", not "Agents".
 */
const matchSidebarItem = (pathname: string): SidebarItem | undefined => {
  let match: SidebarItem | undefined;
  let matchLength = 0;

  for (const [item, route] of Object.entries(SIDEBAR_ROUTES) as [SidebarItem, string][]) {
    if (!route || route === "/") continue;
    const onRoute = pathname === route || pathname.startsWith(`${route}/`);
    if (onRoute && route.length > matchLength) {
      match = item;
      matchLength = route.length;
    }
  }

  return match;
};

const isModifiedNavigationEvent = (
  event: React.MouseEvent<HTMLAnchorElement>
) => event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

const handleSidebarLinkClick = (
  event: React.MouseEvent<HTMLAnchorElement>,
  onClick?: () => void
) => {
  if (!onClick || isModifiedNavigationEvent(event)) {
    return;
  }

  event.preventDefault();
  onClick();
};

// ── Sub-components ───────────────────────────────────────────────────────────

const SidebarButton = ({
  label,
  Icon,
  active,
  onClick,
  href,
  expanded = false,
  hoverContent,
}: NavItem & {
  active?: boolean;
  onClick?: () => void;
  href?: string;
  expanded?: boolean;
  /** Collapsed rail only: shown in a hover card in place of the label tooltip. */
  hoverContent?: React.ReactNode;
}) => {
  const content = (
    <>
      <Icon size={15} />
      <span
        className={cn(
          "inline-block overflow-hidden whitespace-nowrap text-[13px] font-normal transition-[max-width,opacity] duration-200 ease-in-out",
          expanded ? "max-w-32 opacity-100" : "max-w-0 opacity-0"
        )}
      >
        {label}
      </span>
    </>
  );

  const button = href ? (
    <Link
      href={href}
      aria-label={label}
      className={cn(
        "inline-flex h-8 shrink-0 items-center justify-center overflow-hidden rounded-4xl text-[13px] font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 hover:bg-muted hover:text-foreground",
        expanded
          ? "w-full justify-start gap-2 px-2"
          : "w-8 justify-center gap-0 px-0",
        active && "bg-accent text-accent-foreground"
      )}
      onClick={(event) => handleSidebarLinkClick(event, onClick)}
    >
      {content}
    </Link>
  ) : (
    <Button
      aria-label={label}
      className={cn(
        "h-8 overflow-hidden transition-all duration-200 ease-in-out",
        expanded
          ? "w-full justify-start gap-2 px-2"
          : "w-8 justify-center gap-0 px-0",
        active && "bg-accent text-accent-foreground"
      )}
      onClick={onClick}
      size="default"
      type="button"
      variant="ghost"
    >
      {content}
    </Button>
  );

  if (expanded) {
    return button;
  }

  if (hoverContent) {
    return (
      <HoverCard closeDelay={100} openDelay={150}>
        <HoverCardTrigger asChild>{button}</HoverCardTrigger>
        <HoverCardContent align="start" className="w-52 p-1.5" side="right">
          <p className="px-2 pt-1 pb-1.5 text-xs text-muted-foreground">{label}</p>
          {hoverContent}
        </HoverCardContent>
      </HoverCard>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Pinned projects (from the Projects page pin toggle). Nested under the
// Projects item when expanded; inside its hover card when collapsed. The ⋯
// actions menu is expanded-only — a dropdown opened from inside a hover card
// closes the card as soon as the pointer moves onto the menu.
const PinnedProjectLinks = ({
  projects,
  pathname,
  indented,
  userId,
  onNavigate,
}: {
  projects: DbProject[];
  pathname: string;
  indented: boolean;
  userId?: string | null;
  onNavigate?: () => void;
}) => {
  const [unpinningId, setUnpinningId] = useState<string | null>(null);
  const showActions = indented && !!userId;

  const handleUnpin = async (projectId: string) => {
    if (!userId) return;
    setUnpinningId(projectId);
    // Success needs no toast — the row disappearing is the feedback,
    // matching the pin toggle on the Projects page.
    const ok = await unpinProject(userId, projectId);
    if (!ok) toast.error("Failed to unpin project.");
    setUnpinningId(null);
  };

  // Same copy-link behaviour as the project page's share dialog; the link
  // only opens for people who are members of the project.
  const handleShare = async (href: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${href}`);
      toast.success("Link copied.");
    } catch (error) {
      console.error("[PinnedProjectLinks] copy link failed:", error);
      toast.error("Failed to copy link.");
    }
  };

  return (
    <div className="flex w-full flex-col gap-0.5">
      {projects.map((project) => {
        const href = `/projects/${project.id}`;
        const active = pathname === href || pathname.startsWith(`${href}/`);
        return (
          <div key={project.id} className="group/pin relative w-full">
            <Link
              href={href}
              title={project.name}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex h-7 w-full min-w-0 items-center rounded-4xl text-[13px] font-normal text-muted-foreground transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/30 hover:bg-muted hover:text-foreground",
                indented ? "pl-8" : "pl-2",
                showActions ? "pr-7" : "pr-2",
                active && "bg-accent text-accent-foreground"
              )}
              onClick={onNavigate}
            >
              <span className="truncate">{project.name}</span>
            </Link>
            {showActions ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label={`Actions for ${project.name}`}
                    className="absolute top-1/2 right-1.5 size-5 -translate-y-1/2 p-0 text-muted-foreground/50 opacity-0 transition-opacity hover:bg-transparent hover:text-foreground focus-visible:opacity-100 group-hover/pin:opacity-100 data-[state=open]:opacity-100"
                    disabled={unpinningId === project.id}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <EllipsisIcon className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                {/* Same item styling as the sidebar's More menu. */}
                <DropdownMenuContent align="start" className="w-52" side="right">
                  <DropdownMenuItem
                    asChild
                    className="font-normal"
                  >
                    <Link
                      href={projectNewChatHref(project.id)}
                      onClick={onNavigate}
                      className="flex items-center gap-2"
                    >
                      <PlusIcon size={14} className="shrink-0" />
                      New chat
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="font-normal"
                    onSelect={() => void handleShare(href)}
                  >
                    <Share2Icon size={14} className="shrink-0" />
                    Share
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="font-normal"
                    onSelect={() => void handleUnpin(project.id)}
                  >
                    <PinOffIcon size={14} className="shrink-0" />
                    Unpin
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const PinnedWidgetLinks = ({
  widgets,
  pathname,
  indented,
  userId,
  onNavigate,
}: {
  widgets: PinnedWidget[];
  pathname: string;
  indented: boolean;
  userId?: string | null;
  onNavigate?: () => void;
}) => {
  const [unpinningId, setUnpinningId] = useState<string | null>(null);
  const showActions = indented && !!userId;

  const handleUnpin = async (widgetId: string) => {
    if (!userId) return;
    setUnpinningId(widgetId);
    const ok = await unpinWidget(userId, widgetId);
    if (!ok) toast.error("Failed to unpin widget.");
    setUnpinningId(null);
  };

  return (
    <div className="flex w-full flex-col gap-0.5">
      {widgets.map((widget) => {
        const href = `/widgets/${widget.id}`;
        const active = pathname === href || pathname.startsWith(`${href}/`);
        return (
          <div key={widget.id} className="group/pin relative w-full">
            <Link
              href={href}
              title={widget.name}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex h-7 w-full min-w-0 items-center rounded-4xl text-[13px] font-normal text-muted-foreground transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/30 hover:bg-muted hover:text-foreground",
                indented ? "pl-8" : "pl-2",
                showActions ? "pr-7" : "pr-2",
                active && "bg-accent text-accent-foreground"
              )}
              onClick={onNavigate}
            >
              <span className="truncate">{widget.name}</span>
            </Link>
            {showActions ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    aria-label={`Actions for ${widget.name}`}
                    className="absolute top-1/2 right-1.5 size-5 -translate-y-1/2 p-0 text-muted-foreground/50 opacity-0 transition-opacity hover:bg-transparent hover:text-foreground focus-visible:opacity-100 group-hover/pin:opacity-100 data-[state=open]:opacity-100"
                    disabled={unpinningId === widget.id}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <EllipsisIcon className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52" side="right">
                  <DropdownMenuItem
                    className="font-normal"
                    onSelect={() => void handleUnpin(widget.id)}
                  >
                    <PinOffIcon size={14} className="shrink-0" />
                    Unpin
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

// Link variant of SidebarButton — used for workflow route items.
const WorkflowLink = ({
  href,
  label,
  Icon,
  active,
  expanded = false,
}: { href: string; label: string; Icon: React.ElementType; active?: boolean; expanded?: boolean }) => {
  const inner = (
    <Link
      href={href}
      aria-label={label}
      className={cn(
        "flex h-8 shrink-0 items-center rounded-md text-[13px] font-normal transition-colors hover:bg-accent hover:text-accent-foreground overflow-hidden",
        expanded
          ? "w-full justify-start gap-2 px-2"
          : "w-8 justify-center gap-0 px-0",
        active && "bg-accent text-accent-foreground"
      )}
    >
      <Icon size={16} className="shrink-0" />
      <span
        className={cn(
          "inline-block overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-in-out",
          expanded ? "max-w-32 opacity-100" : "max-w-0 opacity-0"
        )}
      >
        {label}
      </span>
    </Link>
  );

  if (expanded) return inner;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{inner}</TooltipTrigger>
      <TooltipContent side="right"><p>{label}</p></TooltipContent>
    </Tooltip>
  );
};

const WorkflowDisabledItem = ({
  label,
  Icon,
  expanded = false,
}: { label: string; Icon: React.ElementType; expanded?: boolean }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div
        aria-disabled="true"
        className={cn(
          "flex h-8 shrink-0 cursor-not-allowed items-center rounded-md text-[13px] text-muted-foreground/40 overflow-hidden",
          expanded ? "w-full justify-start gap-2 px-2" : "w-8 justify-center gap-0 px-0"
        )}
      >
        <Icon size={16} className="shrink-0" />
        <span
          className={cn(
            "inline-block overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ease-in-out",
            expanded ? "max-w-32 opacity-100" : "max-w-0 opacity-0"
          )}
        >
          {label}
        </span>
      </div>
    </TooltipTrigger>
    <TooltipContent side="right"><p>Coming soon</p></TooltipContent>
  </Tooltip>
);

// ── Main sidebar ─────────────────────────────────────────────────────────────

export const AppSidebar = ({
  className,
  activeItem,
  onItemClick,
  user = { name: "User", initials: "U" },
  userRole,
  onProfileClick,
  activeTeamId,
  teams,
  sessionUserId,
  listMode = false,
  workflowsEnabled = false,
  codeSpacesEnabled = false,
  onNotificationsNavigate,
  ...props
}: AppSidebarProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  useEffect(() => {
    // Syncing from localStorage post-mount to avoid an SSR/client hydration mismatch.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsExpanded(loadStoredExpanded());
  }, []);
  const pathname = usePathname();
  const pinnedProjects = usePinnedProjects(sessionUserId);
  const pinnedWidgets = usePinnedWidgets(sessionUserId);
  const [pinnedProjectsOpen, setPinnedProjectsOpen] = useState<boolean>(true);
  const [pinnedWidgetsOpen, setPinnedWidgetsOpen] = useState<boolean>(true);
  useEffect(() => {
    // Post-mount sync, same reason as isExpanded above.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPinnedProjectsOpen(loadStoredPinnedProjectsOpen());
  }, []);
  useEffect(() => {
    // Post-mount sync, same reason as isExpanded above.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPinnedWidgetsOpen(loadStoredPinnedWidgetsOpen());
  }, []);
  const handleTogglePinnedProjects = () => {
    setPinnedProjectsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PINNED_PROJECTS_OPEN_STORAGE_KEY, String(next));
      } catch {
        // Storage unavailable — the toggle still works for this session.
      }
      return next;
    });
  };
  const handleTogglePinnedWidgets = () => {
    setPinnedWidgetsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PINNED_WIDGETS_OPEN_STORAGE_KEY, String(next));
      } catch {
        // Storage unavailable — the toggle still works for this session.
      }
      return next;
    });
  };
  const isPinnedProjectActive = pinnedProjects.some(
    (project) => pathname === `/projects/${project.id}` || pathname.startsWith(`/projects/${project.id}/`)
  );
  const isPinnedWidgetActive = pinnedWidgets.some(
    (widget) => pathname === `/widgets/${widget.id}` || pathname.startsWith(`/widgets/${widget.id}/`)
  );
  const routeItem = useMemo(() => matchSidebarItem(pathname), [pathname]);
  const currentItem = activeItem ?? routeItem;
  const expanded = listMode ? true : isExpanded;

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, String(next));
      return next;
    });
  };
  const visibleTopItems = TOP_ITEMS.filter((item) =>
    item.id !== "code" || codeSpacesEnabled
  );
  const visibleMenuGroups = MENU_GROUPS.map((group) => ({
    heading: group.heading,
    items: group.items.filter((item) =>
      hasRequiredRole(item.id, userRole, item.minimumRole, activeTeamId, teams, sessionUserId)
    ),
  })).filter((group) => group.items.length > 0);
  const visibleSecondaryItems = SECONDARY_NAV_ITEMS.filter((item) =>
    hasRequiredRole(item.id, userRole, item.minimumRole, activeTeamId, teams, sessionUserId)
  );
  const desktopMenuGroups = ["Core", "Resources", "Transfer Requests", "Monitoring", "Admin"]
    .flatMap((heading) => visibleMenuGroups.filter((group) => group.heading === heading));
  const isMenuActive = currentItem ? MENU_ITEM_IDS.has(currentItem) : false;
  const sectionClassName = "flex w-full flex-col items-start gap-1 px-2";
  const separatorClassName = cn(
    "my-1 self-center",
    expanded
      ? "data-horizontal:!w-[calc(100%-1rem)]"
      : "data-horizontal:!w-3"
  );

  const enabledWorkflowItems = WORKFLOWS_NAV.filter(
    (i): i is WorkflowNavEnabled =>
      i.enabled && (!i.minimumRole || roleRank(userRole) >= roleRank(i.minimumRole))
  );
  const disabledWorkflowItems = WORKFLOWS_NAV.filter((i): i is WorkflowNavDisabled => !i.enabled);
  const isWorkflowsActive = pathname.startsWith("/workflows");

  return (
    <aside
      className={cn(
        "border-r border-border bg-background transition-[width] duration-200 ease-in-out",
        listMode
          ? "flex w-full flex-1 min-h-0 flex-col gap-1 overflow-y-auto py-3"
          : "relative shrink-0 self-stretch",
        listMode ? "w-full" : expanded ? "w-48" : "w-12",
        className
      )}
      {...props}
    >
      {/* Inner column */}
      <div className={cn(listMode ? "contents" : "flex h-full flex-col gap-1 overflow-hidden py-3")}>
      {/* Nav */}
      <div className={cn(sectionClassName, "flex-1")}>
        {!listMode ? (
          expanded ? (
            <div className="flex w-full items-center justify-between gap-1 px-1 pb-1">
              <span className="truncate px-1 text-[13px] font-semibold tracking-tight text-foreground">
                Woods AI
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label="Collapse sidebar"
                    className="size-8 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                    onClick={handleToggleExpanded}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <PanelLeftClose size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Collapse sidebar</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label="Expand sidebar"
                  className="h-8 w-8 justify-center gap-0 px-0"
                  onClick={handleToggleExpanded}
                  size="default"
                  type="button"
                  variant="ghost"
                >
                  <PanelLeftOpen size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Expand sidebar</p>
              </TooltipContent>
            </Tooltip>
          )
        ) : null}

        {visibleTopItems.map((item) => (
          <SidebarButton
            key={item.id}
            {...item}
            active={currentItem === item.id}
            expanded={expanded}
            href={getSidebarHref(item.id)}
            onClick={() => onItemClick?.(item.id)}
          />
        ))}

        <Separator className={separatorClassName} />

        {PRIMARY_NAV_ITEMS.map((item) => {
          const showPins = item.id === "projects" && pinnedProjects.length > 0;
          // On "/" home-client only knows the section, so the Projects row
          // stays highlighted; on routed pages the open pinned row takes it.
          const active = currentItem === item.id && !(showPins && isPinnedProjectActive && !activeItem);
          // The mobile Sheet closes through the same callback as the bell.
          const pinnedLinks = showPins ? (
            <PinnedProjectLinks
              indented={expanded}
              onNavigate={onNotificationsNavigate}
              pathname={pathname}
              projects={pinnedProjects}
              userId={sessionUserId}
            />
          ) : null;
          return (
            <div key={item.id} className="contents">
              <div className="group/projects relative w-full">
                <SidebarButton
                  {...item}
                  active={active}
                  expanded={expanded}
                  hoverContent={pinnedLinks ?? undefined}
                  href={getSidebarHref(item.id)}
                  onClick={() => onItemClick?.(item.id)}
                />
                {expanded && showPins ? (
                  <Button
                    aria-controls="sidebar-pinned-projects"
                    aria-expanded={pinnedProjectsOpen}
                    aria-label={pinnedProjectsOpen ? "Hide pinned projects" : "Show pinned projects"}
                    className={cn(
                      "absolute top-1/2 right-1.5 size-5 -translate-y-1/2 p-0 text-muted-foreground/50 transition-opacity hover:bg-transparent hover:text-foreground focus-visible:opacity-100 group-hover/projects:opacity-100",
                      // Stays faintly visible while closed so hidden pins remain discoverable.
                      pinnedProjectsOpen && "opacity-0"
                    )}
                    onClick={handleTogglePinnedProjects}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ChevronRightIcon
                      className={cn("transition-transform duration-200", pinnedProjectsOpen && "rotate-90")}
                      size={12}
                    />
                  </Button>
                ) : null}
              </div>
              {expanded && pinnedProjectsOpen ? (
                <div className="contents" id="sidebar-pinned-projects">
                  {pinnedLinks}
                </div>
              ) : null}
            </div>
          );
        })}

        <Separator className={separatorClassName} />

        {visibleSecondaryItems.map((item) => {
          const showPins = item.id === "widgets" && pinnedWidgets.length > 0;
          const pinnedLinks = showPins ? (
            <PinnedWidgetLinks
              widgets={pinnedWidgets}
              indented={expanded}
              onNavigate={onNotificationsNavigate}
              pathname={pathname}
              userId={sessionUserId}
            />
          ) : null;
          return (
            <div key={item.id} className="contents">
              <div className="group/widgets relative w-full">
                <SidebarButton
                  {...item}
                  active={currentItem === item.id && !(showPins && isPinnedWidgetActive && !activeItem)}
                  expanded={expanded}
                  hoverContent={pinnedLinks ?? undefined}
                  href={getSidebarHref(item.id)}
                  onClick={() => onItemClick?.(item.id)}
                />
                {expanded && showPins ? (
                  <Button
                    aria-controls="sidebar-pinned-widgets"
                    aria-expanded={pinnedWidgetsOpen}
                    aria-label={pinnedWidgetsOpen ? "Hide pinned widgets" : "Show pinned widgets"}
                    className={cn(
                      "absolute top-1/2 right-1.5 size-5 -translate-y-1/2 p-0 text-muted-foreground/50 transition-opacity hover:bg-transparent hover:text-foreground focus-visible:opacity-100 group-hover/widgets:opacity-100",
                      pinnedWidgetsOpen && "opacity-0"
                    )}
                    onClick={handleTogglePinnedWidgets}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <ChevronRightIcon
                      className={cn("transition-transform duration-200", pinnedWidgetsOpen && "rotate-90")}
                      size={12}
                    />
                  </Button>
                ) : null}
              </div>
              {expanded && pinnedWidgetsOpen ? (
                <div className="contents" id="sidebar-pinned-widgets">
                  {pinnedLinks}
                </div>
              ) : null}
            </div>
          );
        })}

        {/* ── Workflows partition ── */}
        {workflowsEnabled ? (
          <>
            <Separator className={separatorClassName} />

            {listMode ? (
              <>
                {enabledWorkflowItems.map((item) => (
                  <WorkflowLink
                    key={item.id}
                    href={item.href}
                    label={item.label}
                    Icon={item.Icon}
                    active={pathname.startsWith(item.href)}
                    expanded={expanded}
                  />
                ))}

                {disabledWorkflowItems.length > 0 ? (
                  <>
                    <div className={cn("my-0.5 h-px bg-border/60", expanded ? "mx-2" : "mx-3")} />
                    {disabledWorkflowItems.map((item) => (
                      <WorkflowDisabledItem
                        key={item.id}
                        label={item.label}
                        Icon={item.Icon}
                        expanded={expanded}
                      />
                    ))}
                  </>
                ) : null}
              </>
            ) : (
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        aria-label="Open workflows menu"
                        className={cn(
                          "h-8 overflow-hidden transition-all duration-200 ease-in-out",
                          expanded
                            ? "w-full justify-start gap-2 px-2"
                            : "w-8 justify-center gap-0 px-0",
                          isWorkflowsActive && "bg-accent text-accent-foreground"
                        )}
                        size="default"
                        type="button"
                        variant="ghost"
                      >
                        <WorkflowIcon size={16} />
                        <span
                          className={cn(
                            "inline-block overflow-hidden whitespace-nowrap text-[13px] font-normal transition-[max-width,opacity] duration-200 ease-in-out",
                            expanded ? "max-w-20 opacity-100" : "max-w-0 opacity-0"
                          )}
                        >
                          Workflows
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  {!expanded ? (
                    <TooltipContent side="right">
                      <p>Workflows</p>
                    </TooltipContent>
                  ) : null}
                </Tooltip>

                <DropdownMenuContent align="end" side="right" className="w-56">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Workflows
                  </DropdownMenuLabel>
                  {enabledWorkflowItems.map((item) => (
                    <DropdownMenuItem key={item.id} asChild>
                      <Link href={item.href} className="flex items-center gap-2">
                        <item.Icon size={14} className="shrink-0" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}

                  {disabledWorkflowItems.length > 0 ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-muted-foreground">
                        Coming soon
                      </DropdownMenuLabel>
                      {disabledWorkflowItems.map((item) => (
                        <DropdownMenuItem key={item.id} disabled>
                          <item.Icon size={14} className="shrink-0" />
                          {item.label}
                        </DropdownMenuItem>
                      ))}
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        ) : null}

        {listMode ? (
          <>
            <Separator className={separatorClassName} />
            {visibleMenuGroups.map((group, groupIndex) => (
              <div key={group.heading} className="flex w-full flex-col gap-1">
                <p className="px-2 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{group.heading}</p>
                {group.items.map((item) => (
                  getSidebarHref(item.id) ? (
                    <Link
                      key={item.id}
                      href={getSidebarHref(item.id)!}
                      aria-label={item.label}
                      className={cn(
                        "inline-flex h-8 w-full shrink-0 items-center justify-start gap-2 overflow-hidden rounded-4xl px-2 text-[13px] font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 hover:bg-muted hover:text-foreground",
                        currentItem === item.id && "bg-accent text-accent-foreground"
                      )}
                      onClick={(event) => handleSidebarLinkClick(event, () => onItemClick?.(item.id))}
                    >
                      {item.Icon ? <item.Icon size={14} className="shrink-0" /> : null}
                      <span className="text-[13px] font-normal">{item.label}</span>
                    </Link>
                  ) : (
                    <Button
                      key={item.id}
                      aria-label={item.label}
                      className={cn(
                        "h-8 w-full justify-start gap-2 px-2",
                        currentItem === item.id && "bg-accent text-accent-foreground"
                      )}
                      onClick={() => onItemClick?.(item.id)}
                      size="default"
                      type="button"
                      variant="ghost"
                    >
                      {item.Icon ? <item.Icon size={14} className="shrink-0" /> : null}
                      <span className="text-[13px] font-normal">{item.label}</span>
                    </Button>
                  )
                ))}
                {groupIndex < visibleMenuGroups.length - 1 ? <Separator className={separatorClassName} /> : null}
              </div>
            ))}
          </>
        ) : null}
      </div>

      {/* Bottom: menu + avatar */}
      <div className={cn(sectionClassName, "gap-2")}>
        {!listMode ? (
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Open menu"
                  className={cn(
                    "h-8 overflow-hidden transition-all duration-200 ease-in-out",
                    expanded
                      ? "w-full justify-start gap-2 px-2"
                      : "w-8 justify-center gap-0 px-0",
                    isMenuActive && "bg-accent text-accent-foreground"
                  )}
                  size="default"
                  type="button"
                  variant="ghost"
                >
                  <ChevronsUpDownIcon size={16} />
                  <span
                    className={cn(
                      "inline-block overflow-hidden whitespace-nowrap text-[13px] font-normal transition-[max-width,opacity] duration-200 ease-in-out",
                      expanded ? "max-w-20 opacity-100" : "max-w-0 opacity-0"
                    )}
                  >
                    More
                  </span>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            {!expanded ? (
              <TooltipContent side="right">
                <p>More</p>
              </TooltipContent>
            ) : null}
          </Tooltip>
          <DropdownMenuContent align="end" side="right" className="scrollbar-minimal w-52">
            <DropdownMenuLabel className="font-normal text-foreground">More</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {desktopMenuGroups.map((group) => (
              <DropdownMenuSub key={group.heading}>
                <DropdownMenuSubTrigger
                  className={cn(
                    "font-normal",
                    group.items.some((item) => item.id === currentItem) &&
                      "bg-accent text-accent-foreground"
                  )}
                >
                  {group.heading}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="scrollbar-minimal max-h-[calc(100dvh-2rem)] w-56 overflow-y-auto">
                  <DropdownMenuLabel className="font-normal text-foreground">
                    {group.heading}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {group.items.map((item) => (
                    getSidebarHref(item.id) ? (
                      <DropdownMenuItem key={item.id} className="font-normal" asChild>
                        <Link
                          href={getSidebarHref(item.id)!}
                          onClick={(event) => handleSidebarLinkClick(event, () => onItemClick?.(item.id))}
                        >
                          {item.Icon ? <item.Icon size={14} className="shrink-0" /> : null}
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        className="font-normal"
                        key={item.id}
                        onClick={() => onItemClick?.(item.id)}
                      >
                        {item.Icon ? <item.Icon size={14} className="shrink-0" /> : null}
                        {item.label}
                      </DropdownMenuItem>
                    )
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        ) : null}

        <SidebarButton
          id="documentation"
          label="Documentation"
          Icon={BookOpenTextIcon}
          active={currentItem === "documentation"}
          expanded={expanded}
          href={getSidebarHref("documentation")}
          onClick={() => onItemClick?.("documentation")}
        />

        <NotificationsBell
          userId={sessionUserId ?? null}
          expanded={expanded}
          onNavigate={onNotificationsNavigate}
        />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="open-profile"
              aria-label={user.name}
              className={cn(
                "h-8 overflow-hidden rounded-full transition-all duration-200 ease-in-out",
                expanded
                  ? "w-full justify-start gap-2 px-0.5"
                  : "w-8 justify-center gap-0 px-0.5"
              )}
              size="default"
              type="button"
              variant="ghost"
              onClick={onProfileClick}
            >
              <Avatar className="size-6">
                <AvatarFallback className="bg-foreground text-background text-xs font-medium">
                  {user.initials}
                </AvatarFallback>
              </Avatar>
              <span
                className={cn(
                  "inline-block overflow-hidden truncate whitespace-nowrap text-[13px] font-normal transition-[max-width,opacity] duration-200 ease-in-out",
                  expanded ? "max-w-24 opacity-100" : "max-w-0 opacity-0"
                )}
              >
                {user.name}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{user.name}</p>
          </TooltipContent>
        </Tooltip>
      </div>
      </div>
    </aside>
  );
};
